import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Switch, InputNumber, Tabs, ColorPicker, Upload } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import RichTextEditor from '../../components/common/RichTextEditor';

interface AdminProjectDto {
    id: string;
    slug: string;
    title: string;
    description: string;
    icon?: string;
    imageUrl?: string;
    color?: string;
    orderIndex: number;
    isActive: boolean;
    availableLanguages: string[];
    translations: { languageCode: string; title: string; description: string }[];
}

export const Projects: React.FC = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

    const { data: projects, isLoading } = useQuery({
        queryKey: ['adminProjects'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/projects');
            return response.data.data || [];
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/projects/${id}`);
        },
        onSuccess: () => {
            message.success('Project deleted successfully.');
            queryClient.invalidateQueries({ queryKey: ['adminProjects'] });
        },
        onError: () => {
            message.error('Failed to delete project.');
        }
    });

    const uploadImageMutation = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('isPublic', 'true');
            formData.append('isActive', 'true');

            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        },
        onSuccess: (data: any) => {
            message.success('Image uploaded successfully.');
            form.setFieldsValue({ imageUrl: `/api/media/download/${data.id || data.Id}` });
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || 'Image upload failed.');
        }
    });

    const handleImageUpload = (file: File) => {
        uploadImageMutation.mutate(file);
        return false;
    };

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = appLanguages.map(lang => {
                const title = values[`translations_${lang}_title`];
                const description = values[`translations_${lang}_description`];
                if (!title) return null;
                return { languageCode: lang, title, description };
            }).filter(Boolean);

            if (translations.length === 0) {
                throw new Error('At least one translation is required.');
            }

            const payload = {
                id: editingProjectId || undefined,
                slug: values.slug,
                icon: values.icon,
                imageUrl: values.imageUrl,
                color: typeof values.color === 'string' ? values.color : values.color?.toHexString(),
                orderIndex: values.orderIndex,
                isActive: values.isActive,
                translations
            };

            if (editingProjectId) {
                await apiClient.put(`/admin/projects/${editingProjectId}`, payload);
            } else {
                await apiClient.post('/admin/projects', payload);
            }
        },
        onSuccess: () => {
            message.success('Project saved successfully.');
            setIsModalVisible(false);
            form.resetFields();
            queryClient.invalidateQueries({ queryKey: ['adminProjects'] });
            queryClient.invalidateQueries({ queryKey: ['publicProjects'] }); // Invalidate public cache too
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || error.message || 'Failed to save project.');
        }
    });

    const handleEdit = (record: AdminProjectDto) => {
        setEditingProjectId(record.id);
        const formValues: any = {
            slug: record.slug,
            icon: record.icon,
            imageUrl: record.imageUrl,
            color: record.color,
            orderIndex: record.orderIndex,
            isActive: record.isActive
        };

        appLanguages.forEach(lang => {
            const trans = record.translations?.find((t: any) => t.languageCode === lang);
            if (trans) {
                formValues[`translations_${lang}_title`] = trans.title;
                formValues[`translations_${lang}_description`] = trans.description;
            }
        });

        form.setFieldsValue(formValues);
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        setEditingProjectId(null);
        form.resetFields();
        form.setFieldsValue({
            isActive: true,
            orderIndex: 0
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this project?',
            content: 'This action cannot be undone.',
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const columns = [
        { title: 'Title', dataIndex: 'title', key: 'title' },
        { title: 'Slug', dataIndex: 'slug', key: 'slug' },
        { title: 'Order', dataIndex: 'orderIndex', key: 'orderIndex' },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => isActive ? <span style={{ color: 'green' }}>Active</span> : <span style={{ color: 'red' }}>Inactive</span>
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: AdminProjectDto) => (
                <Space size="middle">
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: lang.toUpperCase(),
        children: (
            <>
                <Form.Item name={`translations_${lang}_title`} label="Project Title">
                    <Input placeholder={`Title in ${lang.toUpperCase()}`} />
                </Form.Item>
                <Form.Item name={`translations_${lang}_description`} label="Project Description">
                    <RichTextEditor
                        placeholder={`Description in ${lang.toUpperCase()}`}
                        height={240}
                    />
                </Form.Item>
            </>
        )
    }));

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>Projects Management</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                    Create Project
                </Button>
            </div>

            <Table
                dataSource={projects}
                columns={columns}
                rowKey="id"
                loading={isLoading}
            />

            <Modal
                title={editingProjectId ? 'Edit Project' : 'Create Project'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={saveMutation.isPending}
                destroyOnHidden
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)}>
                    <Tabs type="card" items={tabItems} style={{ marginBottom: 24 }} />

                    <Form.Item name="slug" label="URL Slug (e.g., projekt-zukunft)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item label="Image URL (For Project Card)">
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="imageUrl" noStyle>
                                <Input placeholder="e.g. /api/media/download/123e4567..." />
                            </Form.Item>
                            <Upload
                                beforeUpload={handleImageUpload}
                                showUploadList={false}
                                maxCount={1}
                            >
                                <Button icon={<UploadOutlined />} loading={uploadImageMutation.isPending}>
                                    Upload
                                </Button>
                            </Upload>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item name="icon" label="Icon Name (Ant Design or Lucide)">
                        <Input placeholder="e.g. EditOutlined" />
                    </Form.Item>

                    <Space style={{ width: '100%', flexDirection: 'column' }} size="large">
                        <Space style={{ display: 'flex', gap: '24px' }}>
                            <Form.Item name="color" label="Card Background Color">
                                <ColorPicker showText />
                            </Form.Item>
                            <Form.Item name="orderIndex" label="Order Index">
                                <InputNumber min={0} />
                            </Form.Item>
                            <Form.Item name="isActive" label="Is Active" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Space>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};
